<template>
  <div>
  
<div
  class="spreadsheet-container"
  ref="spreadsheetContainer"
  :style="{'--row-h': (fixedRowHeight || 28) + 'px'}"
></div>

  </div>
</template>

<script>
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.css';

export default {
  name: 'SpreadsheetComponentAdvancedB',
  props: {
    buttonLabel: { type: String, required: true },
    width: { type: String, default: '800px' },
    height: { type: String, default: '400px' },
    highlightHeader: { type: Boolean, default: false },
    freezeTopRow: { type: Boolean, default: false },
    autoSaveEnabled: { type: Boolean, default: false },
    downloadPrefix: { type: String, default: 'table' },
 // NEW
  lockRowHeights: { type: Boolean, default: true }, // keep heights constant
  fixedRowHeight: { type: Number,  default: 28 },   // px
},
  data() {
    return {
      hotInstance: null,
      cellClassMap: new Map(),   // key "row,col" -> "class1 class2"
      lastSelection: null,

      // Hard limits (defense in depth)
      MAX_HOT_ROWS: 50000,
      MAX_HOT_COLS: 200,
      MAX_CELL_CHARS: 10000,
      MAX_PASTE_CELLS: 200000,

      onPasteHandler: null,
      onFocusoutHandler: null,
    };
  },

  mounted() {
    const vm = this;

    this.hotInstance = new Handsontable(this.$refs.spreadsheetContainer, {
      data: Array(100).fill().map(() => Array(26).fill('')),
      rowHeaders: true,
      colHeaders: true,

      // Keep data inert (no HTML, no formulas)
      copyPaste: false,         // we own a sanitized paste path below
      // formulas: false,       // leave off unless your HOT build supports this flag

       manualRowResize: false,
      manualColumnResize: true,
      manualColumnMove: true,
      manualRowMove: true,

      minRows: 100,
      minCols: 26,
      colWidths: 100,
      width: this.width,
      height: this.height,
      customBorders: [],
      licenseKey: 'non-commercial-and-evaluation',
      fixedRowsTop: this.freezeTopRow ? 1 : 0,
 // >>> NEW: keep row height constant
  autoRowSize: this.lockRowHeights ? false : true,
  wordWrap: this.lockRowHeights ? false : true,
  className: this.lockRowHeights ? 'htNoWrap' : '',
  rowHeights: this.lockRowHeights ? this.fixedRowHeight : undefined,
  viewportColumnRenderingOffset: 24,
      // PURE meta producer — MUST NOT call HOT APIs like getCellMeta inside here
 
  cells: (row, col) => {
    const key = `${row},${col}`;
    const customClasses = vm.sanitizeClassList(vm.cellClassMap.get(key) || '');
    const classParts = new Set(customClasses.split(/\s+/).filter(Boolean));
    if (vm.highlightHeader && row === 0) classParts.add('header-highlight');

    return {
      className: [...classParts].join(' '),
      type: 'text',
      renderer: Handsontable.renderers.TextRenderer,
      allowHtml: false
    };
  },
      afterSelectionEnd(r1, c1, r2, c2) {
        const rs = Math.min(r1, r2), re = Math.max(r1, r2);
        const cs = Math.min(c1, c2), ce = Math.max(c1, c2);
        vm.lastSelection = [rs, cs, re, ce];
        vm.$emit('update-last-selection', vm.lastSelection);
      },

      afterColumnMove: () => {
        if (vm.autoSaveEnabled) vm.$emit('request-save');
      },
      afterRowMove: () => {
        if (vm.autoSaveEnabled) vm.$emit('request-save');
      },

      contextMenu: {
        items: {
          ...Handsontable.plugins.ContextMenu.DEFAULT_ITEMS,
          separator_fill: Handsontable.plugins.ContextMenu.SEPARATOR,
          recolor_like_cells: {
            name: '🎨 Recolor Like Cells',
            callback() {
              const hot = vm.hotInstance;
              const selected = hot.getSelected();
              if (!selected || !selected.length) {
                alert('Please select a range to recolor.');
                return;
              }

              const [r1, c1, r2, c2] = selected[selected.length - 1];
              const rs = Math.min(r1, r2), re = Math.max(r1, r2);
              const cs = Math.min(c1, c2), ce = Math.max(c1, c2);

              const byValue = new Map();
              for (let r = rs; r <= re; r++) {
                for (let c = cs; c <= ce; c++) {
                  const val = String(hot.getDataAtCell(r, c) ?? '').trim();
                  if (!val) continue;
                  if (!byValue.has(val)) byValue.set(val, []);
                  byValue.get(val).push([r, c]);
                }
              }

              const used = new Set();
              const randHex = () => {
                let hex;
                do {
                  hex = '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
                } while (used.has(hex));
                used.add(hex);
                return hex;
              };

              const newColorClasses = new Set();

              for (const positions of byValue.values()) {
                if (positions.length < 2) continue; // only color repeats
                const hex = randHex();
                if (!vm.isValidHex(hex)) continue;
                const cls = `color-${hex.slice(1)}`;
                newColorClasses.add(cls);

                for (const [r, c] of positions) {
                  const key = `${r},${c}`;
                  const existing = vm.sanitizeClassList(vm.cellClassMap.get(key) || '');
                  const list = new Set(existing.split(/\s+/).filter(Boolean));
                  for (const k of [...list]) if (k.startsWith('color-')) list.delete(k);
                  list.add(cls);
                  const merged = [...list].join(' ');
                  vm.cellClassMap.set(key, merged);
                  hot.setCellMeta(r, c, 'className', merged);
                }
              }

              for (const cls of newColorClasses) {
                const hex = '#' + cls.slice(6);
                if (vm.isValidHex(hex)) vm.ensureColorStyle(hex);
              }

              hot.render();
              if (vm.autoSaveEnabled) vm.$emit('request-save');
            }
          }
        }
      }
    });

    // Focusout notifier
    const el = this.$refs.spreadsheetContainer;
    this.onFocusoutHandler = () => {
      setTimeout(() => {
        const newlyFocused = document.activeElement;
        const stillIn = el.contains(newlyFocused);
        const inToolbar = newlyFocused?.closest?.('.toolbar');
        if (!stillIn && !inToolbar) this.$emit('deselected');
      }, 0);
    };
    el.addEventListener('focusout', this.onFocusoutHandler);

    // Sanitized paste path
    this.onPasteHandler = (evt) => this.onPaste(evt);
    el.addEventListener('paste', this.onPasteHandler);
  },

  beforeUnmount() {
    const el = this.$refs.spreadsheetContainer;
    if (el && this.onPasteHandler) el.removeEventListener('paste', this.onPasteHandler);
    if (el && this.onFocusoutHandler) el.removeEventListener('focusout', this.onFocusoutHandler);
    if (this.hotInstance) this.hotInstance.destroy();
  },

  methods: {
    // -------- Public API ----------
    setData(rows, opts = {}) {
      const {
        resetFormatting = true,
        neutralizeFormulas = true,
        trimWhitespace = true
      } = opts;

      const hot = this.hotInstance;
      if (!hot) return;

      let data = Array.isArray(rows) ? rows : [];
      const cappedRows = Math.min(data.length, this.MAX_HOT_ROWS);
      const maxCols = Math.min(
        this.MAX_HOT_COLS,
        data.reduce((m, r) => Math.max(m, Array.isArray(r) ? r.length : 0), 0)
      );

      const out = new Array(cappedRows);
      for (let r = 0; r < cappedRows; r++) {
        const src = Array.isArray(data[r]) ? data[r] : [];
        const row = new Array(maxCols);
        for (let c = 0; c < maxCols; c++) {
          let s = src[c] == null ? '' : String(src[c]);
          if (trimWhitespace) s = s.trim();
          if (s.length > this.MAX_CELL_CHARS) s = s.slice(0, this.MAX_CELL_CHARS);
          if (neutralizeFormulas && /^[=+\-@]/.test(s)) s = "'" + s;
          row[c] = s;
        }
        out[r] = row;
      }

      if (resetFormatting) this.clearAllColorMeta?.();

      hot.loadData(out);
      hot.render();
    },

    // -------- Safety helpers ----------
    isValidHex(hex) {
      return typeof hex === 'string' && /^#[0-9a-fA-F]{6}$/.test(hex);
    },

    sanitizeClassList(classStr) {
      if (!classStr) return '';
      const allowedSingletons = new Set([
        'header-highlight', 'bold-cell', 'italic-cell',
        'underline-cell', 'strike-cell',
        'align-left', 'align-center', 'align-right',
        'valign-top', 'valign-middle', 'valign-bottom'
      ]);
      return classStr
        .split(/\s+/)
        .filter(Boolean)
        .filter(cls =>
          /^color-[0-9a-fA-F]{6}$/.test(cls) ||
          /^text-[0-9a-fA-F]{6}$/.test(cls) ||
          allowedSingletons.has(cls) ||
          /^font-\d+$/.test(cls)
        )
        .join(' ');
    },

    stripColorTokens(classStr) {
      return (classStr || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter(cls => !cls.startsWith('color-'))
        .join(' ');
    },

    clearAllColorMeta() {
      const hot = this.hotInstance;
      const rows = hot.countRows();
      const cols = hot.countCols();
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const meta = hot.getCellMeta(r, c); // safe now (cells() does NOT call getCellMeta)
          if (!meta || !meta.className) continue;
          const cleaned = this.sanitizeClassList(this.stripColorTokens(meta.className));
          if (cleaned) hot.setCellMeta(r, c, 'className', cleaned);
          else hot.removeCellMeta(r, c, 'className');
        }
      }
      this.cellClassMap.clear();
      hot.render();
    },

    // -------- Selection helpers ----------
    toA1(r, c) {
      const colName = (n) => {
        let s = ''; n++;
        while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
        return s;
      };
      return `${colName(c)}${r + 1}`;
    },

    makeXlsxFilename({ prefix, r1, c1, r2, c2 }) {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const ts = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
      let range = '';
      if ([r1, c1, r2, c2].every(Number.isInteger)) {
        const a1 = this.toA1(r1, c1);
        const a2 = this.toA1(r2, c2);
        range = `_${a1}-${a2}`;
      }
      return `${(prefix || this.downloadPrefix || 'table')}_${ts}_${rand}${range}.xlsx`;
    },

    normalizeSelection(sel) {
      if (!sel) return null;
      const hot = this.hotInstance;
      let [r1, c1, r2, c2] = sel;
      if (r1 > r2) [r1, r2] = [r2, r1];
      if (c1 > c2) [c1, c2] = [c2, c1];
      const maxR = Math.max(0, hot.countRows() - 1);
      const maxC = Math.max(0, hot.countCols() - 1);
      r1 = Math.max(0, r1); c1 = Math.max(0, c1);
      r2 = Math.min(maxR, r2); c2 = Math.min(maxC, c2);
      return [r1, c1, r2, c2];
    },

    getUsedRange() {
      const hot = this.hotInstance;
      const rows = hot.countRows();
      const cols = hot.countCols();
      let minR = Infinity, minC = Infinity, maxR = -1, maxC = -1;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const v = hot.getDataAtCell(r, c);
          if (v != null && String(v).trim() !== '') {
            if (r < minR) minR = r; if (c < minC) minC = c;
            if (r > maxR) maxR = r; if (c > maxC) maxC = c;
          }
        }
      }
      for (const key of this.cellClassMap.keys()) {
        const cls = this.sanitizeClassList(this.cellClassMap.get(key) || '');
        if (!cls || !cls.split(/\s+/).some(s => s.startsWith('color-'))) continue;
        const [rStr, cStr] = key.split(',');
        const r = +rStr, c = +cStr;
        if (r < minR) minR = r; if (c < minC) minC = c;
        if (r > maxR) maxR = r; if (c > maxC) maxC = c;
      }
      if (maxR < 0 || maxC < 0) return null;
      return [minR, minC, maxR, maxC];
    },

    getBgHex(row, col) {
      const cls = this.sanitizeClassList(this.cellClassMap.get(`${row},${col}`) || '');
      const colorCls = cls.split(/\s+/).find(c => /^color-[0-9a-fA-F]{6}$/.test(c));
      return colorCls ? ('#' + colorCls.slice(6)) : null;
    },

    // -------- Safe style injection ----------
    ensureColorStyle(hex) {
      if (!this.isValidHex(hex)) return;
      const className = `color-${hex.slice(1)}`;
      const styleTagId = 'dynamic-color-styles';
      let styleTag = document.getElementById(styleTagId);
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = styleTagId;
        document.head.appendChild(styleTag);
      }
      const rule = `
.handsontable .htCore td.${className} { background-color: ${hex} !important; }
`;
      if (!styleTag.textContent.includes(`td.${className}`)) {
        styleTag.textContent += rule;
      }
    },

    // Only accept whitelisted class names when setting a range
    setRangeClass(r1, c1, r2, c2, className) {
      const hot = this.hotInstance;
      if (!hot) return;

      const ok =
        /^color-[0-9a-fA-F]{6}$/.test(className) ||
        /^text-[0-9a-fA-F]{6}$/.test(className) ||
        ['bold-cell','italic-cell','underline-cell','strike-cell','align-left','align-center','align-right','valign-top','valign-middle','valign-bottom'].includes(className) ||
        /^font-\d+$/.test(className);
      if (!ok) return;

      const rs = Math.max(0, Math.min(r1, r2));
      const re = Math.max(0, Math.max(r1, r2));
      const cs = Math.max(0, Math.min(c1, c2));
      const ce = Math.max(0, Math.max(c1, c2));

      for (let r = rs; r <= re; r++) {
        for (let c = cs; c <= ce; c++) {
          const key = `${r},${c}`;
          const existing = this.sanitizeClassList(this.cellClassMap.get(key) || '');
          const list = new Set(existing.split(/\s+/).filter(Boolean));
          for (const k of [...list]) if (k.startsWith('color-')) list.delete(k);
          list.add(className);
          const merged = [...list].join(' ');
          this.cellClassMap.set(key, merged);
          hot.setCellMeta(r, c, 'className', merged);
        }
      }
      hot.render();
    },

    clearRecolors() {
      this.cellClassMap.clear();
      this.hotInstance.render();
    },

    // -------- Sanitized paste path ----------
    onPaste(event) {
      event.preventDefault();
      const cd = event.clipboardData || window.clipboardData;
      const text = cd ? cd.getData('Text') : '';
      this.insertParsedPastedData(text);
    },

    insertParsedPastedData(pastedData) {
      const trimmed = String(pastedData || '').trim();
      if (!trimmed) return;

      const rows = this.customSplit(trimmed).map(row =>
        row.split('\t').map(cell => {
          let s = cell == null ? '' : String(cell);
          s = s.trim();
          if (s.length > this.MAX_CELL_CHARS) s = s.slice(0, this.MAX_CELL_CHARS);
          if (/^[=+\-@]/.test(s)) s = "'" + s; // keep inert
          return s;
        })
      );

      // Total cells cap
      const total = rows.reduce((n, r) => n + r.length, 0);
      if (total > this.MAX_PASTE_CELLS) this.truncate2D(rows, this.MAX_PASTE_CELLS);

      // HOT shape caps
      const maxRows = Math.min(rows.length, this.MAX_HOT_ROWS);
      const maxCols = Math.min(
        this.MAX_HOT_COLS,
        rows.reduce((m, r) => Math.max(m, r.length), 0)
      );

      const out = new Array(maxRows);
      for (let r = 0; r < maxRows; r++) {
        const src = rows[r] || [];
        const row = new Array(maxCols);
        for (let c = 0; c < maxCols; c++) row[c] = src[c] ?? '';
        out[r] = row;
      }

      const sel = this.hotInstance.getSelected();
      if (!sel || !sel.length) {
        alert('Please select a starting cell before pasting.');
        return;
      }
      const [startRow, startCol] = sel[0];

      this.hotInstance.populateFromArray(startRow, startCol, out, undefined, undefined, 'paste');
    },

    truncate2D(rows, maxCells) {
      let count = 0;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        for (let j = 0; j < r.length; j++) {
          count++;
          if (count > maxCells) {
            rows[i] = r.slice(0, j);
            rows.length = i + 1;
            return;
          }
        }
      }
    },

    customSplit(input) {
      const rows = [];
      let cur = '';
      let inQ = false;
      for (let i = 0; i < input.length; i++) {
        const ch = input[i];
        const next = i < input.length - 1 ? input[i + 1] : '';
        cur += ch;
        if (ch === '"' && next !== '"') {
          inQ = !inQ;
        } else if (ch === '\n' && !inQ) {
          rows.push(cur.trim());
          cur = '';
        }
      }
      if (cur.length > 0) rows.push(cur.trim());
      return rows.filter(Boolean);
    },

    // Utility
    getData() { return this.hotInstance.getData(); },
    loadData(data) { this.hotInstance.loadData(data); },
    addRows() {
      const cur = this.hotInstance.getData();
      const next = Math.min(cur.length + 100, this.MAX_HOT_ROWS);
      const existingCols = Math.max(...cur.map(r => r.length), 26);
      const need = next - cur.length;
      if (need <= 0) return;
      const newData = cur.concat(Array(need).fill().map(() => Array(existingCols).fill('')));
      this.hotInstance.loadData(newData);
    },
  }
};
</script>

<style>
.spreadsheet-container {
  margin: 20px;
  border: 1px solid #ccc;
  overflow: hidden;
}
button {
  display: block;
  margin: 20px auto;
  padding: 10px 20px;
  font-size: 16px;
}/* Keep each cell single-line and avoid height growth while scrolling */
.spreadsheet-container .handsontable .htCore td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: var(--row-h, 28px);
}

/* Handsontable’s class also disables wrapping; keep it for safety */
.spreadsheet-container .htNoWrap td {
  white-space: nowrap;
}

</style>
